import React from "react";

// Abstract, minimal, 2px-stroke SVG glyphs for each industry.
// No photography. Data-systems / logistics visual language only.

const base = {
  width: "100%",
  height: "100%",
  viewBox: "0 0 320 200",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": true,
};

const stroke = { stroke: "currentColor", strokeWidth: 2, strokeLinecap: "square", strokeLinejoin: "round" };
const copper = { stroke: "#D65A1C", strokeWidth: 2, fill: "#D65A1C" };
const copperStroke = { stroke: "#D65A1C", strokeWidth: 2, fill: "none" };

// 01 · Agricultural Exporters — stacked containers with connecting logistics lines
const Exporters = () => (
  <svg {...base}>
    <g {...stroke} opacity="0.55">
      <rect x="30" y="130" width="60" height="30" />
      <rect x="30" y="100" width="60" height="30" />
      <rect x="90" y="130" width="60" height="30" />
      <rect x="90" y="100" width="60" height="30" />
      <rect x="90" y="70" width="60" height="30" />
      <line x1="60" y1="100" x2="60" y2="130" />
      <line x1="120" y1="70" x2="120" y2="130" />
    </g>
    <g {...copperStroke}>
      <path d="M170 90 L220 90 L220 60 L280 60" />
      <path d="M170 130 L240 130 L240 160 L280 160" />
    </g>
    <g fill="#D65A1C">
      <circle cx="170" cy="90" r="3.5" />
      <circle cx="220" cy="60" r="3.5" />
      <circle cx="280" cy="60" r="3.5" />
      <circle cx="170" cy="130" r="3.5" />
      <circle cx="240" cy="160" r="3.5" />
      <circle cx="280" cy="160" r="3.5" />
    </g>
  </svg>
);

// 02 · Packhouses — parallel packing lines with grading nodes
const Packhouses = () => (
  <svg {...base}>
    <g {...stroke} opacity="0.55">
      <line x1="30" y1="60" x2="290" y2="60" />
      <line x1="30" y1="100" x2="290" y2="100" />
      <line x1="30" y1="140" x2="290" y2="140" />
    </g>
    <g fill="#D65A1C">
      {[60, 100, 140].flatMap((y, r) => [70, 130, 190, 250].map((x, c) => (
        <circle key={`${r}-${c}`} cx={x} cy={y} r={c === r ? 5 : 3} opacity={c === r ? 1 : 0.6} />
      )))}
    </g>
    <g {...copperStroke}>
      <rect x="20" y="45" width="20" height="110" />
      <rect x="280" y="45" width="20" height="110" />
    </g>
  </svg>
);

// 03 · Grower Organisations — hub-and-spoke network
const Growers = () => (
  <svg {...base}>
    <g stroke="currentColor" strokeWidth="1" opacity="0.55" fill="none">
      <line x1="160" y1="100" x2="60" y2="50" />
      <line x1="160" y1="100" x2="60" y2="150" />
      <line x1="160" y1="100" x2="260" y2="50" />
      <line x1="160" y1="100" x2="260" y2="150" />
      <line x1="160" y1="100" x2="90" y2="100" />
      <line x1="160" y1="100" x2="230" y2="100" />
    </g>
    <circle cx="160" cy="100" r="14" {...copperStroke} />
    <circle cx="160" cy="100" r="4" fill="#D65A1C" />
    <g fill="currentColor" opacity="0.8">
      <circle cx="60" cy="50" r="3.5" />
      <circle cx="60" cy="150" r="3.5" />
      <circle cx="260" cy="50" r="3.5" />
      <circle cx="260" cy="150" r="3.5" />
      <circle cx="90" cy="100" r="3.5" />
      <circle cx="230" cy="100" r="3.5" />
    </g>
  </svg>
);

// 04 · Export Agents — relay diagram
const Agents = () => (
  <svg {...base}>
    <g {...stroke} opacity="0.55">
      <rect x="30" y="80" width="60" height="40" />
      <rect x="130" y="80" width="60" height="40" />
      <rect x="230" y="80" width="60" height="40" />
    </g>
    <g {...copperStroke}>
      <path d="M90 100 L130 100" />
      <path d="M190 100 L230 100" />
      <path d="M120 100 L128 96 M120 100 L128 104" />
      <path d="M220 100 L228 96 M220 100 L228 104" />
    </g>
    <g fill="#D65A1C">
      <circle cx="60" cy="100" r="4" />
      <circle cx="160" cy="100" r="4" />
      <circle cx="260" cy="100" r="4" />
    </g>
  </svg>
);

// 05 · Regulated Organisations — layered documents with certification tick
const Regulators = () => (
  <svg {...base}>
    <g {...stroke} opacity="0.5">
      <rect x="80" y="65" width="120" height="90" />
      <rect x="95" y="50" width="120" height="90" />
      <rect x="110" y="35" width="120" height="90" />
    </g>
    <g {...stroke} opacity="0.5">
      <line x1="125" y1="60" x2="215" y2="60" />
      <line x1="125" y1="75" x2="215" y2="75" />
      <line x1="125" y1="90" x2="200" y2="90" />
    </g>
    <g {...copperStroke}>
      <circle cx="230" cy="130" r="20" />
      <path d="M220 130 L228 138 L242 122" />
    </g>
  </svg>
);

const map = {
  "agricultural-exporters": Exporters,
  packhouses: Packhouses,
  "grower-organisations": Growers,
  "export-agents": Agents,
  "regulated-organisations": Regulators,
};

export const IndustryGlyph = ({ slug, className = "" }) => {
  const Comp = map[slug] || Exporters;
  return (
    <div className={`w-full h-full flex items-center justify-center text-[color:var(--wald-slate)] dark:text-[color:var(--wald-mist)] ${className}`}>
      <Comp />
    </div>
  );
};
